import { encounterCreatureField } from '../shared/array-types';
import { normalizePhpListish } from '../shared/php-listish';
import { readCreatureStatuses, type AuraOwnerStateResolver } from '../janus-engine/compile/creature-status-reader';
import { projectAuraOwnerSkills } from '../janus-engine/compile/aura-owner-skill-projection';
import { readLayerListEntryUuid } from '../janus-engine/compile/inventory-raw-readers';

type Row = Record<string, unknown>;
declare global { interface Window { ftBankAuraOwnerSkills?: (id: string, leaves: Row) => number } }
const record = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(v ?? '').toUpperCase();
const idOf = (row: Row): string => upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));

/** The install seam shared by local resource/status tools, their observer frames,
 * raw row patches and departure. No fetch, compiler or scene operation occurs here. */
export function updateAuraOwnerRecipients(encounter: Row, ownerIds: readonly string[],
    read = { value: ftLayerValue, entries: ftLayerEntries,
        definition: (id: string): Row | null => record(window.FateTide?.objectCache?.get(id)) }): Map<string, string[]> {
    const rows = normalizePhpListish(encounter.encounter_creatures_arr).map(record).filter((row): row is Row => row !== null);
    const owners = new Map(rows.map(row => [idOf(row), row]));
    const resolveOwner: AuraOwnerStateResolver = id => owners.get(upper(id)) ?? null;
    const changed = new Map<string, string[]>();
    const changedOwners = new Set(ownerIds.map(upper));
    for (const holder of rows) {
        const creature = record(record(holder.computed)?.creature);
        if (!creature) continue;
        const definition = (id: string): Row | null => record(record(holder.catalog)?.[upper(id)]) ?? read.definition(upper(id));
        const statuses = Object.values(readCreatureStatuses(holder, 'target', definition)).map(record).filter((row): row is Row => row !== null);
        for (const ownerId of changedOwners) {
            if (!statuses.some(row => upper(readLayerListEntryUuid(row.status_caster_id)) === ownerId)) continue;
            const prior = record(creature.skills) ?? {};
            const result = projectAuraOwnerSkills({ holder, ownerId, skills: prior, definition, resolveOwner,
                layerValue: read.value, layerEntries: read.entries });
            if (!result.ok) {
                window.dispatchEvent(new CustomEvent('ft:unported', { detail: { reason: result.reason, creatureId: idOf(holder) } }));
                continue;
            }
            if (!result.changed) continue;
            creature.skills = result.skills;
            const paths = changed.get(idOf(holder)) ?? [];
            const leaves: Row = {};
            for (const key of new Set([...Object.keys(prior), ...Object.keys(result.skills)]))
                if (prior[key] !== result.skills[key]) { paths.push(`computed.creature.skills.${key}`); leaves[key] = result.skills[key]; }
            const memoSkillLeavesCopied = window.ftBankAuraOwnerSkills?.(idOf(holder), leaves) ?? 0;
            changed.set(idOf(holder), paths);
            window.dispatchEvent(new CustomEvent('ft:aura-owner-skills-applied', { detail: {
                ownerId, holderId: idOf(holder), paths, objectsCopied: result.objectsCopied ?? 0, memoSkillLeavesCopied,
            } }));
        }
    }
    return changed;
}

export function auraOwnerInstalled(encounterId: string, ids: readonly string[]): void {
    const encounter = window.FateTide?.encounterCache?.get(encounterId) ?? window.FateTide?.encounterCache?.get(upper(encounterId));
    if (!encounter) return;
    const changes = updateAuraOwnerRecipients(encounter as Row, ids);
    if (changes.size) window.ftPatchAppliedCasterViews?.(changes, '');
}
window.addEventListener('ft:encounter-rows-patched', event => {
    const detail = (event as CustomEvent).detail;
    if (detail?.encounterId) auraOwnerInstalled(detail.encounterId, [...detail.creatureIds ?? [], ...detail.removedIds ?? []]);
});
window.addEventListener('ft:truth-frame-applied', event => {
    const frame = (event as CustomEvent).detail;
    if (!frame?.encounter_id || frame.cache_ref) return;
    const ids = [...(frame.rows ?? []), ...(frame.deletes ?? [])]
        .filter((row: Row) => row.table === 'encounter_creatures').map((row: Row) => upper(row.row_id));
    for (const row of frame.rows ?? []) if (row.table === 'statuses') ids.push(upper(row.row?.status_target_id));
    ids.push(...Object.keys(frame.computed ?? {}), ...Object.keys(frame.member_revs ?? {}));
    auraOwnerInstalled(frame.encounter_id, ids);
});
